// packages/ui/src/components/activity/EventRow.tsx
// Single event row with icon, timestamp, summary, severity badge, and host tag

import type { AuditEvent } from '../../api/client'

const EVENT_ICONS: Record<string, string> = {
  'tool:call': '\u{1F527}',
  'tool:result': '\u{2705}',
  'tool:error': '\u{274C}',
  'session:started': '\u{25B6}',
  'session:ended': '\u{23F9}',
  'card:created': '\u{2795}',
  'card:updated': '\u{270F}',
  'card:moved': '\u{27A1}',
  'agent:activity': '\u{1F916}',
  'approval:requested': '\u{1F514}',
  'approval:granted': '\u{1F44D}',
  'approval:denied': '\u{1F6AB}',
}

const SEVERITY_COLORS: Record<string, string> = {
  info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  warn: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
  critical: 'bg-red-600/20 text-red-300 border-red-600/30',
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getSeverity(event: AuditEvent): string {
  if (event.eventType.includes('error') || event.eventType.includes('denied')) return 'error'
  if (event.eventType.includes('warn')) return 'warn'
  if (event.eventType.includes('critical')) return 'critical'
  return 'info'
}

function getSummary(event: AuditEvent): string {
  if (event.tool) {
    return `${event.eventType}: ${event.tool}`
  }
  return event.eventType
}

function getHost(event: AuditEvent): string | undefined {
  return event.params?.host as string | undefined
}

export interface EventRowProps {
  event: AuditEvent
  onClick?: (event: AuditEvent) => void
  isSelected?: boolean
}

export function EventRow({ event, onClick, isSelected }: EventRowProps) {
  const severity = getSeverity(event)
  const icon = EVENT_ICONS[event.eventType] ?? '\u{1F4CB}'
  const host = getHost(event)

  return (
    <div
      data-testid={`event-row-${event.id}`}
      className={`flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-gray-800 border-l-2 border-l-blue-500'
          : 'hover:bg-gray-800/50'
      }`}
      onClick={() => onClick?.(event)}
    >
      <span className="text-base shrink-0" data-testid="event-icon">{icon}</span>

      <span className="text-xs text-gray-500 shrink-0 w-36" data-testid="event-timestamp">
        {formatTimestamp(event.createdAt)}
      </span>

      <span className="text-sm text-gray-200 flex-1 truncate" data-testid="event-summary">
        {getSummary(event)}
      </span>

      <span
        data-testid={`severity-${severity}`}
        className={`text-xs px-1.5 py-0.5 rounded border shrink-0 ${SEVERITY_COLORS[severity]}`}
      >
        {severity}
      </span>

      {host && (
        <span
          data-testid="event-host"
          className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded shrink-0"
        >
          {host}
        </span>
      )}
    </div>
  )
}
